import { Options } from 'qr-code-styling';
import {
  DetailedHTMLProps,
  ImgHTMLAttributes,
  useEffect,
  useState,
} from 'react';

import useStyles from './QRCode.styles';

const defaultOptions: Options = {
  width: 240,
  height: 240,
  margin: 0,
  cornersSquareOptions: {
    type: 'extra-rounded',
  },
  dotsOptions: {
    type: 'rounded',
  },
  backgroundOptions: {
    color: 'transparent',
  },
};

interface QRCodeProps
  extends DetailedHTMLProps<
    ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  > {
  data: string;
  size: number;
  options?: Omit<Options, 'data' | 'width' | 'height'>;
  isOnDark?: boolean;
}

export default function QRCode({
  data,
  size,
  options,
  isOnDark = false,
  ...imgProps
}: QRCodeProps) {
  const [dataURL, setDataURL] = useState<string | undefined>();
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    // generating 3x image to make sure rounded corners look good on high resolution displays
    width: size * 3,
    height: size * 3,
    data,
  };
  const { classes, cx } = useStyles({ isOnDark });

  useEffect(() => {
    const createQRCode = async () => {
      const QR = (await import('qr-code-styling')).default;
      const qrCode = new QR(mergedOptions);

      qrCode
        .getRawData('png')
        .then((blob) => {
          if (blob instanceof Blob) {
            return URL.createObjectURL(blob);
          }
        })
        .then(setDataURL)
        .catch(console.log);
    };

    createQRCode();
  }, [data, size, options]);

  if (!dataURL) return null;
  return (
    <img
      {...{
        ...imgProps,
        alt: imgProps.alt || 'QR Code',
        width: size,
        height: size,
      }}
      src={dataURL}
      className={cx(classes.img, imgProps.className)}
    />
  );
}
